import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useRpcData } from "@redotech/react-util/rpc-data";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import LinkExternal02Icon from "@redotech/redo-icons/link-external-02.svg";
import { TeamType } from "@redotech/redo-model/team";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputProvider } from "@redotech/ui/form";
import { memo, useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { RedoAdminClientContext } from "../../client/context";
import { getTeam, updateTeam } from "../../client/team";
import { automationUrl } from "../merchant-app";
import { LoadTeamContext, TeamContext } from "../team";
import * as styles from "./logistics.module.css";
import {
  LogisticsGeneralCard,
  logisticsGeneralDefault,
  logisticsGeneralForm,
} from "./logistics/general";
import { LocationsCard } from "./logistics/locations-card";
import { LogisticsProductSummary } from "./logistics/logistics-product-summary";
import { SettingCard } from "./returns-package-protection/v2/component/setting-card";
import { SettingSection } from "./returns-package-protection/v2/component/setting-section";
import { SettingSectionTabNav } from "./returns-package-protection/v2/component/setting-section-tab-nav";
import {
  redoManagedReturnsFormValueToTeam,
  RedoManagedReturnsSettingsCardV2,
  redoManagedReturnsSettingsDefault,
  redoManagedReturnsSettingsForm,
  redoManagedReturnsTeamToFormValue,
} from "./returns-package-protection/v2/redo-managed-returns-settings";
import { TabNavigationProvider } from "./returns-package-protection/v2/tab-navigation-provider";

const reFulfillmentForm = groupInput({
  enabled: input<boolean>(),
  revSharePercentage: input<number>(),
});

type ReFulfillmentValue = InputProvider.Value<typeof reFulfillmentForm>;

const reFulfillmentDefault: ReFulfillmentValue = {
  enabled: false,
  revSharePercentage: 50,
};

interface SkippedAutomationInfo {
  locationName: string;
  automationId: string;
  threePlName: string;
}

const logisticsForm = groupInput(
  {
    general: logisticsGeneralForm,
    redoManagedReturnsSettings: redoManagedReturnsSettingsForm,
    reFulfillment: reFulfillmentForm,
    customsBrokerClientNumber: input<string>(),
  },
  {
    validator: (value) => {
      if (
        value.redoManagedReturnsSettings.enabled &&
        value.redoManagedReturnsSettings.availableReturnsCenters.length === 0
      ) {
        return [
          "At least one returns center must be configured when Redo Managed Returns is enabled",
        ];
      }
      return [];
    },
  },
);

type LogisticsValue = InputProvider.Value<typeof logisticsForm>;

const logisticsDefault: LogisticsValue = {
  general: logisticsGeneralDefault,
  redoManagedReturnsSettings: redoManagedReturnsSettingsDefault,
  reFulfillment: reFulfillmentDefault,
  customsBrokerClientNumber: "",
};

export const LogisticsPage = memo(function LogisticsPage() {
  const params = useParams();
  const teamId = params.teamId!;

  const team = useContext(TeamContext);
  const loadTeam = useRequiredContext(LoadTeamContext);
  const client = useRequiredContext(RedoAdminClientContext);
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const { value: automationsData } = useRpcData(
    rpcClient,
    "getRmrAutomations",
    { input: { teamId } },
  );

  const { value: returnsCentersData } = useRpcData(
    rpcClient,
    "getReturnsCenters",
    { input: { sort: null } },
  );

  const automationByReturnsCenterId = useMemo(() => {
    const automationByAccountId = new Map<
      string,
      { automationId: string; threePlName: string }
    >();
    for (const {
      thirdPartyAccountId,
      automationId,
      threePlName,
    } of automationsData?.automations ?? []) {
      automationByAccountId.set(thirdPartyAccountId, {
        automationId,
        threePlName,
      });
    }

    const map = new Map<
      string,
      { automationId: string; threePlName: string; locationName: string }
    >();
    for (const center of returnsCentersData?.returnsCenters ?? []) {
      if (!center.thirdPartyAccountId || !center.locationName) continue;
      const automation = automationByAccountId.get(center.thirdPartyAccountId);
      if (automation) {
        map.set(center.id, {
          ...automation,
          locationName: center.locationName,
        });
      }
    }
    return map;
  }, [automationsData, returnsCentersData]);

  const [skippedAutomations, setSkippedAutomations] = useState<
    SkippedAutomationInfo[] | null
  >(null);

  const value: LogisticsValue | undefined = useMemo(() => {
    if (!team) {
      return;
    }

    return {
      general: {
        marketplace: !!team.settings.recommerce?.marketplace,
        consignmentPercentage: team.settings.recommerce?.consignmentPercentage,
      },
      redoManagedReturnsSettings: redoManagedReturnsTeamToFormValue(team),
      reFulfillment: {
        enabled: !!team.settings.reFulfillment?.enabled,
        revSharePercentage:
          team.settings.reFulfillment?.revSharePercentage ?? 50,
      },
      customsBrokerClientNumber: team.customsBrokerClientNumber ?? "",
    };
  }, [team]);

  const input = useInput(logisticsForm, value || logisticsDefault);

  const handleCloseSkipped = useHandler(() => setSkippedAutomations(null));

  const save: PageFormSave = useHandler(async (signal) => {
    const currentTeam = await getTeam(client, { teamId, signal });
    const formValue = input.value;

    const previouslyEnabledIds = new Set(
      redoManagedReturnsTeamToFormValue(currentTeam)
        .availableReturnsCenters.filter((c) => c.refulfillment)
        .map((c) => c.returnsCenterId),
    );

    const skipped: SkippedAutomationInfo[] = [];
    for (const center of formValue.redoManagedReturnsSettings
      .availableReturnsCenters) {
      if (
        center.refulfillment &&
        !previouslyEnabledIds.has(center.returnsCenterId)
      ) {
        const automation = automationByReturnsCenterId.get(
          center.returnsCenterId,
        );
        if (automation) {
          skipped.push({
            locationName: automation.locationName,
            automationId: automation.automationId,
            threePlName: automation.threePlName,
          });
        }
      }
    }

    currentTeam.settings.recommerce = {
      ...currentTeam.settings.recommerce,
      marketplace: formValue.general.marketplace,
      consignmentPercentage: formValue.general.consignmentPercentage,
    };

    currentTeam.settings.redoManagedReturns = redoManagedReturnsFormValueToTeam(
      formValue.redoManagedReturnsSettings,
    );

    currentTeam.redoManagedReturnsPricePerOrder =
      formValue.redoManagedReturnsSettings.coverageShare || undefined;

    currentTeam.settings.reFulfillment = {
      ...currentTeam.settings.reFulfillment,
      enabled: formValue.reFulfillment.enabled,
      revSharePercentage: formValue.reFulfillment.revSharePercentage,
    };

    currentTeam.customsBrokerClientNumber =
      formValue.customsBrokerClientNumber || undefined;

    await updateTeam(client, { teamId, team: currentTeam, signal });

    loadTeam();
    if (skipped.length > 0) {
      setSkippedAutomations(skipped);
    }
  });

  return (
    <>
      {value && (
        <LogisticsForm id={teamId} input={input} save={save} value={value} />
      )}
      {skippedAutomations && skippedAutomations.length > 0 && (
        <SkippedAutomationsModal
          onClose={handleCloseSkipped}
          skippedAutomations={skippedAutomations}
          teamId={teamId}
        />
      )}
    </>
  );
});

const LogisticsForm = memo(function LogisticsForm({
  id,
  input,
  save,
  value,
}: {
  id: string;
  input: InputProvider.Form<typeof logisticsForm>;
  save: PageFormSave;
  value: LogisticsValue | undefined;
}) {
  const team = useContext(TeamContext);
  const is3pl = team?.teamType === TeamType.FulfillmentService;

  const {
    general,
    redoManagedReturnsSettings,
    reFulfillment,
    customsBrokerClientNumber,
  } = input.inputs;

  const enabledProducts = useMemo(() => {
    return [
      general.value.marketplace,
      redoManagedReturnsSettings.value.enabled,
    ].filter(Boolean);
  }, [general.value.marketplace, redoManagedReturnsSettings.value.enabled]);

  return (
    <TabNavigationProvider>
      <div className={styles.pageContainer}>
        <SettingSectionTabNav />
        <div className={styles.settingsContent}>
          <PageForm
            initial={value || logisticsDefault}
            input={input}
            save={save}
          >
            <SettingSection description="" id="products" title="Products">
              <SettingCard>
                <Flex align="stretch" dir="column" gap="none">
                  <Flex align="stretch" dir="column" gap="none" p="xl">
                    <Text fontSize="sm" fontWeight="medium" textColor="primary">
                      {`${enabledProducts.length} ${enabledProducts.length === 1 ? "product" : "products"} enabled`}
                    </Text>
                  </Flex>
                </Flex>
                <Flex align="stretch" dir="column" gap="none">
                  <LogisticsProductSummary
                    description="List products on the marketplace"
                    productEnabled={general.value.marketplace}
                    setProductEnabled={general.inputs.marketplace.setValue}
                    title="Recommerce / Liquidation"
                  />
                  <LogisticsProductSummary
                    description="Charge merchants for managed returns processing"
                    productEnabled={redoManagedReturnsSettings.value.enabled}
                    setProductEnabled={
                      redoManagedReturnsSettings.inputs.enabled.setValue
                    }
                    title="Redo Managed Returns"
                  />
                </Flex>
              </SettingCard>
            </SettingSection>
            <SettingSection
              description="Configure recommerce and liquidation settings for returned items"
              id="recommerce-liquidation"
              title="Recommerce / Liquidation"
            >
              <LogisticsGeneralCard input={general} />
            </SettingSection>
            <SettingSection
              description="Configure Redo managed returns processing settings"
              id="redo-managed-returns"
              title="Redo Managed Returns"
            >
              <RedoManagedReturnsSettingsCardV2
                input={redoManagedReturnsSettings}
                teamId={id}
              />
              {input.allErrors.length > 0 && (
                <Text fontSize="sm" textColor="error">
                  {input.allErrors[0]}
                </Text>
              )}
            </SettingSection>
            <SettingSection
              description="Configure re-fulfillment billing settings for returned items fulfilled from Returns Centers"
              id="re-fulfillment"
              title="Re-fulfillment"
            >
              <RedoIncrementDecrement
                description="Percentage of fulfilled order value charged as Redo's revenue share"
                label="Revenue Share Percentage"
                max={100}
                min={0}
                setValue={(value) =>
                  reFulfillment.inputs.revSharePercentage.setValue(value)
                }
                suffix="%"
                value={reFulfillment.value.revSharePercentage}
              />
            </SettingSection>
            <SettingSection
              description="Configure customs and cross-border settings"
              id="cross-border"
              title="Cross-Border"
            >
              <RedoTextInput
                label="Customs Broker Client Number"
                setValue={customsBrokerClientNumber.setValue}
                value={customsBrokerClientNumber.value}
              />
            </SettingSection>
          </PageForm>
          {is3pl && (
            <SettingSection
              description="Manage which locations are designated as returns centers"
              id="locations"
              title="Locations"
            >
              <LocationsCard teamId={id} />
            </SettingSection>
          )}
        </div>
      </div>
    </TabNavigationProvider>
  );
});

const SkippedAutomationsModal = memo(function SkippedAutomationsModal({
  skippedAutomations,
  teamId,
  onClose,
}: {
  skippedAutomations: SkippedAutomationInfo[];
  teamId: string;
  onClose: () => void;
}) {
  const automationGroups = useMemo(() => {
    const map = new Map<
      string,
      { automationId: string; threePlName: string; locationNames: string[] }
    >();
    for (const {
      automationId,
      threePlName,
      locationName,
    } of skippedAutomations) {
      const existing = map.get(automationId);
      if (existing) {
        existing.locationNames.push(`${threePlName} - ${locationName}`);
      } else {
        map.set(automationId, {
          automationId,
          threePlName,
          locationNames: [`${threePlName} - ${locationName}`],
        });
      }
    }
    return [...map.values()];
  }, [skippedAutomations]);

  return (
    <RedoModal
      onModalCloseRequested={onClose}
      primaryButton={{ text: "Dismiss", onClick: onClose, size: "xs" }}
      subtitle="New Shopify locations will be created by the background workflow. The automations below already exist and must be manually updated to include the new locations."
      title={
        <Flex align="center" gap="xs">
          <AlertTriangleIcon
            style={{
              color: "var(--redo-colors-warning-400)",
              flexShrink: 0,
              height: "1.1em",
              width: "1.1em",
            }}
          />
          <span>Some automations need manual update</span>
        </Flex>
      }
    >
      <Flex dir="column" gap="md">
        {automationGroups.map(
          ({ automationId, threePlName, locationNames }) => (
            <Flex dir="column" gap="xs" key={automationId}>
              <a
                href={automationUrl(teamId, automationId)}
                rel="noreferrer"
                style={{
                  alignItems: "center",
                  display: "inline-flex",
                  fontSize: "var(--redo-font-size-sm)",
                  fontWeight: "var(--redo-font-weight-medium)",
                  gap: "var(--redo-spacing-xs)",
                }}
                target="_blank"
              >
                {threePlName}
                <LinkExternal02Icon
                  style={{ height: "1em", width: "1em", flexShrink: 0 }}
                />
              </a>
              {locationNames.map((name) => (
                <Text fontSize="sm" key={name} textColor="tertiary">
                  • {name}
                </Text>
              ))}
            </Flex>
          ),
        )}
      </Flex>
    </RedoModal>
  );
});
